/**
 * Exit-poll answers wait on the phone until they reach the server (polling-day networks drop): saved at
 * once, sent in batches, kept until the server took them; an answer sent twice is counted once (its id).
 * Plain TypeScript with the browser pieces injected, so it can be tested in Node.
 */
import { Emitter } from "@core/emitter";
import type { ExitAnswer, FieldApi } from "./api";

export interface KeyValue {
  get(k: string): string | null;
  set(k: string, v: string | null): void;
}

export class ExitQueue {
  readonly changes = new Emitter();
  sending = false;
  lastError: unknown = null;
  constructor(
    private readonly d: { api: FieldApi; assemblyId: () => string | null; kv: KeyValue; online?: () => boolean; onUnauthenticated?: (e: unknown) => void }
  ) {}

  private key() {
    return `exit.q.${this.d.assemblyId() ?? "-"}`;
  }
  get queued(): ExitAnswer[] {
    try {
      return JSON.parse(this.d.kv.get(this.key()) ?? "[]") as ExitAnswer[];
    } catch {
      return [];
    }
  }
  private save(q: ExitAnswer[]) {
    this.d.kv.set(this.key(), q.length ? JSON.stringify(q) : null);
    this.changes.emit();
  }

  add(a: ExitAnswer) {
    this.save([...this.queued, a]);
    void this.flush();
  }

  async flush(): Promise<void> {
    const aid = this.d.assemblyId();
    if (this.sending || !aid || (this.d.online && !this.d.online())) return;
    this.sending = true;
    this.changes.emit();
    try {
      for (;;) {
        const q = this.queued;
        if (!q.length) break;
        const batch = q.slice(0, 100);
        try {
          await this.d.api.exitSend(aid, batch);
          this.lastError = null;
        } catch (e) {
          this.lastError = e;
          const st = (e as { status?: number })?.status;
          if (st === 401) this.d.onUnauthenticated?.(e);
          // a batch the server refuses as wrong (400) would block the rest forever: drop it
          if (st === 400) {
            const sent = new Set(batch.map((x) => x.clientId));
            this.save(this.queued.filter((x) => !sent.has(x.clientId)));
            continue;
          }
          break;
        }
        const sent = new Set(batch.map((x) => x.clientId));
        this.save(this.queued.filter((x) => !sent.has(x.clientId)));
      }
    } finally {
      this.sending = false;
      this.changes.emit();
    }
  }
}
